import {ApolloProvider} from '@apollo/client';
import {ThemeProvider, CssBaseline} from '@mui/material';
import {BrowserRouter} from 'react-router';
import {getTheme} from '@/theme/theme';
import {apolloClient} from '@/lib/apolloClient';
import {AppRoutes} from '@/routes/AppRoutes';
import {useAuthBootstrap} from '@/state/useAuthBootstrap';
import {useOfflineQueueHydration} from '@/state/useOfflineQueueHydration';
import {useRoutePrefetch} from '@/state/useRoutePrefetch';
import {useOfflineSync} from '@/state/useOfflineSync';
import {useOfflinePhotoSync} from '@/state/useOfflinePhotoSync';
import {useRunTicketReconciliation} from '@/state/useRunTicketReconciliation';
import {useTankDropReconciliation} from '@/state/useTankDropReconciliation';
import {NotificationSnackbar} from '@/components/NotificationSnackbar.tsx';
import {resolvedThemeModeAtom, systemPrefersDarkAtom, subscribeSystemTheme} from '@/state/themeAtom';
import {memo, useEffect, useMemo} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';

/**
 * Split out from App so hooks here (useOfflineSync in particular)
 * run *inside* ApolloProvider/BrowserRouter — useApolloClient() only
 * works for descendants of the provider. useRunTicketReconciliation
 * needs the router too (it navigates from a notification's action), and
 * NotificationSnackbar is mounted here — not inside AppRoutes — so it's
 * global across every route, the same way BottomNav/TopNav's own
 * always-visible chips are.
 *
 * useOfflinePhotoSync (added 2026-08-20, full offline-resilient run
 * ticket photo upload) is its own hook rather than folded into
 * useOfflineSync — see that hook's own doc comment for why.
 */
// AppInner re-renders on every tick of the global background hooks below
// (useOfflineSync's 10s poll, useCognitoReachable's 5s recheck, query updates).
// Those only concern the hooks themselves — memoizing the prop-less children
// stops each tick from cascading a full re-render through every page and
// control (which, among other things, reset open date pickers to their
// starting month every few seconds).
const MemoAppRoutes = memo(AppRoutes);
const MemoNotificationSnackbar = memo(NotificationSnackbar);

function AppInner() {
   useAuthBootstrap();
   useOfflineQueueHydration();
   useOfflineSync();
   useOfflinePhotoSync();
   useRunTicketReconciliation();
   // Chunk 7 (2026-09-30) — the unexplained tank-drop flag, admin/office
   // only. See that hook's own doc comment; mounted globally for the same
   // "works no matter what page you're on" reason as useRunTicketReconciliation
   // above.
   useTankDropReconciliation();
   // See useRoutePrefetch.ts's own doc comment — warms every
   // Admin/SuperAdmin-only lazy route chunk once auth resolves, so
   // navigating to one offline hits an already-resolved React.lazy()
   // cache instead of a failed fetch. The equivalent for field-reachable
   // routes (Daily Report/Run Tickets/Barrel Tests) is
   // useFieldRoutePrefetch.ts, triggered from WellsListPage itself
   // rather than here — see that hook's own doc comment for why.
   // RouteErrorBoundary in routes/AppRoutes.tsx is the safety net for
   // whatever neither one gets a chance to cover.
   useRoutePrefetch();
   return (
      <>
         <MemoAppRoutes />
         <MemoNotificationSnackbar />
      </>
   );
}

export function App() {
   const setSystemPrefersDark = useSetAtom(systemPrefersDarkAtom);
   useEffect(() => subscribeSystemTheme(setSystemPrefersDark), [setSystemPrefersDark]);

   const mode = useAtomValue(resolvedThemeModeAtom);
   const theme = useMemo(() => getTheme(mode), [mode]);

   return (
      <ThemeProvider theme={theme}>
         <CssBaseline />
         <ApolloProvider client={apolloClient}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
               <BrowserRouter>
                  <AppInner />
               </BrowserRouter>
            </LocalizationProvider>
         </ApolloProvider>
      </ThemeProvider>
   );
}
